import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const MAX_VISIBLE = 5;
const ELLIPSIS_WIDTH = 40;

const useOverflow = ({ containerRef, itemRefs, stepsCount }) => {
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const widthsRef = useRef([]);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container || stepsCount === 0) return;

    const containerWidth = container.getBoundingClientRect().width;

    // Use cached width for hidden items (offsetWidth returns 0 when display:none)
    // to prevent oscillation when ResizeObserver fires after items are hidden
    const widths = itemRefs.current.map((ref, i) => {
      if (!ref) return widthsRef.current[i] || 0;
      const w = Math.ceil(ref.getBoundingClientRect().width);
      return w > 0 ? w : widthsRef.current[i] || 0;
    });
    widthsRef.current = widths;

    // If 5 or fewer items, check if they all fit
    if (stepsCount <= MAX_VISIBLE) {
      const totalWidth = widths.reduce((sum, w) => sum + w, 0);
      if (totalWidth <= containerWidth) {
        setVisibleStartIndex(0);
        return;
      }
    }

    // Walk backward from last item, accumulate widths
    let accumulated = 0;
    let startIdx = stepsCount;

    for (let i = stepsCount - 1; i >= 0; i--) {
      const itemWidth = widths[i] || 0;
      const visibleCount = stepsCount - i;
      const needsEllipsis = i > 0;
      const availableWidth = needsEllipsis ? containerWidth - ELLIPSIS_WIDTH : containerWidth;

      if (accumulated + itemWidth > availableWidth || visibleCount > MAX_VISIBLE) {
        break;
      }

      accumulated += itemWidth;
      startIdx = i;
    }

    // Always show at least the last item
    if (startIdx >= stepsCount) {
      startIdx = stepsCount - 1;
    }

    setVisibleStartIndex((prev) => (prev === startIdx ? prev : startIdx));
  }, [containerRef, itemRefs, stepsCount]);

  const rafId = useRef(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    measure();

    const ro = new ResizeObserver(() => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(measure);
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [measure]);

  return { visibleStartIndex };
};

export default useOverflow;
